import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import comms from '../../comms';
import {
  DEFAULT_OTP_POLICY,
  resolveOtpPolicy,
  isServerVerified,
  sanitizeOtpInput,
  isOtpComplete,
  matchesOtpCode,
  normalizePhone,
  otpErrorMessage,
  isTerminalOtpError,
} from '../../../utils/tripPaymentOtp';

/**
 * The driver-assisted passenger verification dialogue, as one reusable state machine
 * (DRIVERAPP_OTP_SPEC.md — "Implement one reusable dialogue for both driver modes").
 *
 * Live Dispatch and Itinerary drive identical UI from this hook; only the owning
 * screen's trip plumbing differs. The hook owns every socket call, both verification
 * modes, and the documented retry/navigation rules; the view is presentational.
 *
 * Steps:
 *   phone   → driver types the passenger's number, we look up contacts
 *   contact → driver picks a match, or names a new contact (both possible)
 *   code    → code has been texted; driver enters what the passenger read out
 *   done    → passenger assigned to the trip (NOT paid — the owner now collects)
 *
 * Three invariants the spec is emphatic about, all enforced here:
 *
 * 1. In client mode AD returns the expected code purely so the app can compare it. It
 *    is held in a ref, never in React state, so it cannot reach the rendered tree, and
 *    it is never persisted or logged.
 * 2. In server mode there is no local code at all; the entered value goes to AD and
 *    only AD's success advances the dialogue. Local approval is impossible.
 * 3. The policy of the ISSUED challenge wins over the fleet capability, which may
 *    change mid-dialogue.
 */

export const OTP_STEP = Object.freeze({
  PHONE: 'phone',
  CONTACT: 'contact',
  CODE: 'code',
  DONE: 'done',
});

/** AD drops a challenge after five rejected attempts; then only a resend can help. */
const MAX_CODE_ATTEMPTS = 5;

export default function usePassengerOtpDialogue({
  tripId,
  /** Fleet `otp` capability from payment state — the INITIAL policy only. */
  otpCapability = null,
  /** Called with the assigned contact once AD confirms; the owner requests collection. */
  onVerified,
  /** Called with the server result for a terminal payment error (already paid, pending…). */
  onPaymentStateChanged,
} = {}) {
  const [step, setStep] = useState(OTP_STEP.PHONE);
  const [phone, setPhoneState] = useState('');
  const [contacts, setContacts] = useState([]);
  const [canCreate, setCanCreate] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [newName, setNewName] = useState('');
  const [code, setCodeState] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [contact, setContact] = useState(null);
  /** Policy of the challenge actually in hand; falls back to the fleet capability. */
  const [challengePolicy, setChallengePolicy] = useState(null);
  const [attempts, setAttempts] = useState(0);

  // Never state: the client-mode comparison value and the challenge id must not be
  // rendered. Refs also survive re-render without re-triggering effects.
  const expectedCodeRef = useRef(null);
  const challengeIdRef = useRef(null);
  // Guards every async reply: a reply for an abandoned dialogue must be discarded.
  const dialogueRef = useRef(0);

  const capabilityPolicy = useMemo(
    () => (otpCapability ? resolveOtpPolicy(otpCapability) : DEFAULT_OTP_POLICY),
    [otpCapability]
  );
  // The issued challenge is authoritative once one exists (§"Default and optional
  // behavior": store the policy WITH the challenge, not as a global assumption).
  const policy = challengePolicy || capabilityPolicy;
  const serverVerified = isServerVerified(policy);

  /** Forget the challenge without leaving the dialogue — used by resend and by edits. */
  const clearChallenge = useCallback(() => {
    expectedCodeRef.current = null;
    challengeIdRef.current = null;
    setChallengePolicy(null);
    setCodeState('');
    setAttempts(0);
  }, []);

  const reset = useCallback(() => {
    dialogueRef.current += 1; // invalidate every reply still in flight
    clearChallenge();
    setStep(OTP_STEP.PHONE);
    setPhoneState('');
    setContacts([]);
    setCanCreate(false);
    setSelectedContactId(null);
    setNewName('');
    setContact(null);
    setBusy(false);
    setError(null);
    setNotice(null);
  }, [clearChallenge]);

  // A changed trip (reassignment, next waypoint) invalidates the whole dialogue: its
  // challenge is bound to fleet + vehicle + trip and means nothing for another trip.
  useEffect(() => { reset(); }, [tripId, reset]);

  /**
   * Shared failure handling for every command. Terminal payment errors leave the
   * dialogue entirely so the owner can re-read state; everything else keeps the form
   * open for correction, which is the documented behaviour.
   */
  const handleFailure = useCallback((result) => {
    setError(otpErrorMessage(result));
    if (isTerminalOtpError(result)) {
      onPaymentStateChanged?.(result);
      return true;
    }
    if (result?.code === 'OTP_EXPIRED') {
      // Challenge is gone server-side; only a fresh code can succeed.
      clearChallenge();
      setNotice('Send a new code to continue.');
    }
    if (result?.code === 'CONTACT_UNAVAILABLE') {
      // The chosen contact no longer matches this number — repeat the lookup.
      setStep(OTP_STEP.PHONE);
      setSelectedContactId(null);
    }
    return false;
  }, [clearChallenge, onPaymentStateChanged]);

  /** Step 1 — look up contacts for the typed number. Sends no SMS. */
  const lookup = useCallback(async () => {
    const normalized = normalizePhone(phone);
    if (!normalized) {
      setError('Enter a ten-digit phone number.');
      return;
    }
    const token = ++dialogueRef.current;
    setBusy(true);
    setError(null);
    setNotice(null);
    const result = await comms.lookupTripPaymentContacts({ tripId, phone: normalized });
    if (token !== dialogueRef.current) return; // dialogue moved on
    setBusy(false);
    if (!result?.success) {
      handleFailure(result);
      return;
    }
    const matches = Array.isArray(result.contacts) ? result.contacts : [];
    setContacts(matches);
    setCanCreate(!!result.canCreate);
    // Even a single match is shown for explicit selection — never auto-chosen.
    setSelectedContactId(null);
    setNewName('');
    // The lookup reply refreshes the fleet capability, but only while no challenge
    // has been issued; an outstanding challenge keeps its own policy.
    if (result.otp && !challengeIdRef.current) setChallengePolicy(null);
    setStep(OTP_STEP.CONTACT);
  }, [phone, tripId, handleFailure]);

  /**
   * Step 2 — create/select the contact and text the code.
   *
   * `resend` reuses the contact already chosen and replaces the previous challenge,
   * which AD invalidates on its side for the same dialogue scope.
   */
  const sendCode = useCallback(async ({ resend = false } = {}) => {
    const normalized = normalizePhone(phone);
    if (!normalized) {
      setError('Enter a ten-digit phone number.');
      return;
    }
    const chosen = resend
      ? (contact?.contactId || selectedContactId)
      : selectedContactId;
    // Creating requires the server's permission; selecting requires a choice.
    if (!chosen && !canCreate) {
      setError('Select the passenger for this number.');
      return;
    }
    const token = ++dialogueRef.current;
    setBusy(true);
    setError(null);
    setNotice(null);
    const result = await comms.requestTripPaymentOtp({
      tripId,
      phone: normalized,
      contactId: chosen || undefined,
      name: chosen ? undefined : newName,
    });
    if (token !== dialogueRef.current) return;
    setBusy(false);
    if (!result?.success) {
      // SMS_DELIVERY_FAILED must NOT look like success: stay on contact selection.
      if (result?.code === 'SMS_DELIVERY_FAILED') {
        clearChallenge();
        setStep(OTP_STEP.CONTACT);
      }
      if (result?.code === 'CONTACT_SELECTION_REQUIRED') setStep(OTP_STEP.CONTACT);
      handleFailure(result);
      return;
    }
    // Policy travels with the challenge: digits/verification sit at the reply's top level.
    const issued = resolveOtpPolicy({
      digits: result.digits,
      verification: result.verification,
      expiresInSeconds: result.expiresInSeconds,
    });
    setChallengePolicy(issued);
    challengeIdRef.current = result.challengeId || null;
    // Present only in client mode. Held in a ref so it never reaches the DOM.
    expectedCodeRef.current = issued.verification === 'client' && result.code !== undefined
      ? String(result.code)
      : null;
    if (result.contact) setContact(result.contact);
    setCodeState('');
    setAttempts(0);
    setNotice(resend ? 'A new code has been sent.' : null);
    setStep(OTP_STEP.CODE);
  }, [phone, contact, selectedContactId, canCreate, newName, tripId, handleFailure, clearChallenge]);

  /** Step 3 — verify the entered code and assign the passenger. */
  const confirm = useCallback(async () => {
    const entered = sanitizeOtpInput(code, policy);
    if (!isOtpComplete(entered, policy)) {
      setError(`Enter the ${policy.digits}-digit code.`);
      return;
    }
    if (!challengeIdRef.current) {
      setError('That code has expired. Send a new one.');
      return;
    }

    // Client mode compares locally FIRST; a mismatch never reaches the server.
    if (!serverVerified) {
      if (!matchesOtpCode(entered, expectedCodeRef.current, policy)) {
        setError('That code is incorrect. Check with the passenger and try again.');
        setCodeState('');
        return;
      }
    }

    const token = ++dialogueRef.current;
    setBusy(true);
    setError(null);
    setNotice(null);
    const result = await comms.confirmTripPaymentContact({
      tripId,
      challengeId: challengeIdRef.current,
      // Mutually exclusive by mode: AD rejects `confirmed` in server mode outright.
      ...(serverVerified ? { code: entered } : { confirmed: true }),
    });
    if (token !== dialogueRef.current) return;
    setBusy(false);

    if (!result?.success) {
      if (result?.code === 'OTP_INVALID') {
        const used = attempts + 1;
        setAttempts(used);
        setCodeState('');
        if (used >= MAX_CODE_ATTEMPTS) {
          // AD has now discarded the challenge; only a resend can recover.
          clearChallenge();
          setError('Too many incorrect attempts. Send a new code.');
          return;
        }
        setError('That code is incorrect. Check with the passenger and try again.');
        return;
      }
      handleFailure(result);
      return;
    }

    // Consumed server-side; keep nothing that could be replayed.
    expectedCodeRef.current = null;
    challengeIdRef.current = null;
    const assigned = result.contact || contact;
    setContact(assigned);
    setStep(OTP_STEP.DONE);
    // Confirmation deliberately does not open a page — the owner collects next.
    onVerified?.(assigned, result);
  }, [code, policy, serverVerified, tripId, attempts, contact, handleFailure, clearChallenge, onVerified]);

  /** Editing the number invalidates any challenge bound to the previous one. */
  const setPhone = useCallback((value) => {
    setPhoneState(value);
    setError(null);
    if (challengeIdRef.current) clearChallenge();
    if (step !== OTP_STEP.PHONE) {
      setStep(OTP_STEP.PHONE);
      setContacts([]);
      setCanCreate(false);
      setSelectedContactId(null);
      setContact(null);
    }
  }, [step, clearChallenge]);

  const setCode = useCallback((value) => {
    setCodeState(sanitizeOtpInput(value, policy));
    setError(null);
  }, [policy]);

  /** Choosing a different contact discards a challenge issued for the previous one. */
  const selectContact = useCallback((id) => {
    setSelectedContactId(id);
    setError(null);
    if (challengeIdRef.current) clearChallenge();
  }, [clearChallenge]);

  const backToContacts = useCallback(() => {
    clearChallenge();
    setStep(OTP_STEP.CONTACT);
    setError(null);
    setNotice(null);
  }, [clearChallenge]);

  return {
    step,
    phone,
    setPhone,
    contacts,
    canCreate,
    selectedContactId,
    selectContact,
    newName,
    setNewName,
    code,
    setCode,
    contact,
    policy,
    serverVerified,
    busy,
    error,
    notice,
    codeComplete: isOtpComplete(code, policy),
    lookup,
    sendCode,
    confirm,
    reset,
    backToContacts,
  };
}
